/**
 * 网站布局组件
 * 包含 Header 和 Footer
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Layout } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMenuDataSimple } from '../../../../hooks/useMenuDataSimple';
import { getNavigationMenu } from '../../../../utils/menuDataConverter';
import { useCommonConfigStore } from '../../../../store/commonConfigStore';
import './WebsiteLayout.css';

const { Header, Footer } = Layout;

interface WebsiteLayoutProps {
    children: React.ReactNode;
    currentPath?: string;
}

const WebsiteLayout: React.FC<WebsiteLayoutProps> = ({ children, currentPath: externalPath }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { contactInfo, websiteInfo, recordInfo } = useCommonConfigStore();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navBoxRef = useRef<HTMLDivElement>(null);
    
    // 从数据库获取菜单数据
    const { menuData, loading: menuLoading } = useMenuDataSimple();
    const navigationMenu = useMemo(() => {
        const navMenu = getNavigationMenu(menuData);
        return navMenu;
    }, [menuData]);

    // 从路径获取当前页面key
    const getCurrentPath = () => {
        if (externalPath) return externalPath;
        const path = location.pathname;
        // 处理 /show/showcase 或 /show/showcases 的情况
        if (path.includes('/show/showcase')) return 'showcase';
        // 处理其他路径
        const match = path.match(/\/show\/([^\/]+)/);
        return match ? match[1] : 'main';
    };
    
    const currentPath = getCurrentPath();

    // 判断路径是否激活
    const isActive = (path: string) => {
        const pathKey = path.replace('/show/', '') || 'main';
        // 处理 showcase 和 showcases 的匹配
        if (pathKey === 'showcase' || pathKey === 'showcases') {
            return currentPath === 'showcase' || currentPath === 'showcases';
        }
        return pathKey === currentPath;
    };

    // 切换移动端菜单
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // 点击菜单项后关闭移动端菜单
    const handleMenuClick = () => {
        setIsMobileMenuOpen(false);
    };

    // 点击外部区域关闭菜单
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (navBoxRef.current && !navBoxRef.current.contains(event.target as Node)) {
                setIsMobileMenuOpen(false);
            }
        };

        if (isMobileMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);

    // 路由变化时关闭菜单
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    return (
        <Layout className="showcase-website">
            <Header className="website-header">
                <div className="header-container">
                    <div className="logo-title-box">
                        <div className="logo-box">
                            <img
                                src="/images/log.png"
                                alt="黄石经济技术开发区·铁山区知识产权服务业集聚区"
                                className="logo-img"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    target.style.visibility = 'hidden';
                                }}
                                onLoad={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = '';
                                    target.style.visibility = 'visible';
                                }}
                            />
                        </div>
                        <div className="title-box">
                            <div className="title-main">黄石经济技术开发区·铁山区知识产权服务业集聚区</div>
                            <div className="title-english">
                                Huangshi Economic & Tech Development Zone · Tieshan District IP Service Cluster
                            </div>
                        </div>
                    </div>

                    <div className="nav-box" ref={navBoxRef}>
                        <ul className={`nav-list ${isMobileMenuOpen ? 'nav-open' : ''}`}>
                            {navigationMenu.length === 0 && (
                                <li style={{ color: '#999', padding: '10px' }}>
                                    菜单加载中...
                                </li>
                            )}
                            {navigationMenu.map(item => {
                                const key = item.path.replace('/show/', '') || 'main';
                                const active = isActive(item.path);
                                
                                // 判断图标类型：HTML实体格式（&#xe开头）或Font Awesome类名
                                const isHtmlEntity = item.icon && (item.icon.startsWith('&#xe') || item.icon.startsWith('&#x'));
                                const isFontAwesome = item.icon && (item.icon.startsWith('fas ') || item.icon.startsWith('fa ') || item.icon.startsWith('fab '));
                                
                                return (
                                    <li key={key} className={active ? 'active' : ''}>
                                        <Link 
                                            to={item.path} 
                                            className={active ? 'active' : ''}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleMenuClick();
                                                // 使用 navigate 进行导航
                                                navigate(item.path);
                                            }}
                                            onTouchStart={(e) => {
                                                // 阻止事件冒泡，但允许默认行为
                                            }}
                                            onMouseDown={(e) => {
                                                // 阻止事件冒泡，但允许默认行为
                                            }}
                                        >
                                            {item.icon && (
                                                <>
                                                    {isHtmlEntity ? (
                                                        <i className="iconfont" dangerouslySetInnerHTML={{ __html: item.icon }} />
                                                    ) : isFontAwesome ? (
                                                        <i className={item.icon} />
                                                    ) : (
                                                        <i className="iconfont" dangerouslySetInnerHTML={{ __html: item.icon }} />
                                                    )}
                                                </>
                                            )}
                                            <span>{item.label}</span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                        
                        {/* 移动端菜单按钮 */}
                        <button 
                            className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
                            onClick={toggleMobileMenu}
                            aria-label="切换菜单"
                        >
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                    </div>
                </div>
            </Header>

            <div className="website-content">
                {children}
            </div>

            <Footer className="website-footer">
                <div className="footer-content">
                    <div className="footer-main">
                        <div className="footer-info">
                            <div className="footer-line">
                                <span className="footer-label">本站主办单位：</span>
                                <span className="footer-value">{websiteInfo.hostUnit || '黄石经济技术开发区·铁山区知识产权服务业集聚区'}</span>
                                <span className="footer-label">版权所有：</span>
                                <span className="footer-value">{websiteInfo.copyright || '黄石经济技术开发区·铁山区知识产权服务业集聚区'}</span>
                            </div>
                            <div className="footer-line">
                                <span className="footer-label">联系电话：</span>
                                <span className="footer-value">{contactInfo.officePhone || '0714-1234567'}</span>
                                <span className="footer-label">地址：</span>
                                <span className="footer-value">{contactInfo.contactAddress || '黄石市经济技术开发区知识产权服务业集聚区'}</span>
                                <span className="footer-label">邮编：</span>
                                <span className="footer-value">{contactInfo.postalCode || '435000'}</span>
                            </div>
                            <div className="footer-line">
                                <span className="footer-label">{recordInfo.icpRecord || '鄂ICP备2024000000号-1'}</span>
                                <span className="footer-label">网站标识码：{recordInfo.websiteId || '4202000000'}</span>
                                <span className="footer-label">鄂公网安备：{recordInfo.policeRecord || '42020000000000'}</span>
                            </div>
                        </div>
                        {/*<div className="footer-icons">
                            <a
                                href="https://www.huangshi.gov.cn/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="footer-icon-link"
                            >
                                <div className="footer-icon-item">
                                    <div className="icon-box government-icon">
                                        <div className="icon-content">政</div>
                                    </div>
                                    <span className="icon-text">政府网站</span>
                                </div>
                            </a>
                            <a
                                href="https://rsj.huangshi.gov.cn/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="footer-icon-link"
                            >
                                <div className="footer-icon-item">
                                    <div className="icon-box institution-icon">
                                        <div className="icon-content">事</div>
                                    </div>
                                    <span className="icon-text">事业单位</span>
                                </div>
                            </a>
                        </div>*/}
                    </div>
                </div>
            </Footer>
        </Layout>
    );
};

export default WebsiteLayout;

